import * as i0 from '@angular/core';
import { NgModule, Injectable } from '@angular/core';
import { catchError, map, of, firstValueFrom, throwError, BehaviorSubject } from 'rxjs';
import { round } from 'mathjs';
import moment from 'moment-timezone';
import * as i1 from '@tangilla/tg-ng-core';
import * as i1$1 from '@angular/forms';
import { Validators } from '@angular/forms';
import { RecnPaymentMethodType } from '@tangilla/tg-ng-models/src/lib/models';
import { CreditCardValidators } from 'angular-cc-library';
import { RequestParameters, EMAIL_VALIDATOR_REGEXP, RecnPaymentMethodType as RecnPaymentMethodType$1, PaymentStepStatus } from '@tangilla/tg-ng-models';
import * as i2 from '@tangilla/tg-ng-core/src/lib/services';
import * as i3 from '@tangilla/tg-ng-core/src/lib/auth';
import * as i4 from '@tangilla/tg-ng-layout';
import * as i5 from '@angular/platform-browser';

class PaymentServicesModule {
    static forRoot(environment, projectConfig) {
        return {
            ngModule: PaymentServicesModule,
            providers: [
                {
                    provide: 'env',
                    useValue: environment
                },
                {
                    provide: 'projectConfig',
                    useValue: projectConfig
                }
            ]
        };
    }
    static forChild(environment, projectConfig) {
        return {
            ngModule: PaymentServicesModule,
            providers: [
                {
                    provide: 'env',
                    useValue: environment
                },
                {
                    provide: 'projectConfig',
                    useValue: projectConfig
                }
            ]
        };
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentServicesModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "16.2.12", ngImport: i0, type: PaymentServicesModule }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentServicesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentServicesModule, decorators: [{
            type: NgModule,
            args: [{}]
        }] });

class PaymentService {
    constructor(addressApiService) {
        this.addressApiService = addressApiService;
    }
    oneInvoiceTotal(invoiceCharges) {
        return round(invoiceCharges.fees.reduce((prevValue, { price, tax }) => prevValue + price + tax, 0) +
            invoiceCharges.optional_fees.filter(optFee => optFee.checked).reduce((prevValue, { price, tax }) => prevValue + price + tax, 0), 2);
    }
    invoicesTotal(invoicesCharges) {
        return invoicesCharges.reduce((pv, cv) => pv + this.oneInvoiceTotal(cv), 0);
    }
    getInvoicesChargesInformation(invoicesCharges) {
        const invoices = invoicesCharges.map(invoiceCharge => {
            return {
                invoice_id: invoiceCharge.invoice_id,
                optional_fees: invoiceCharge.optional_fees.filter(optFee => optFee.checked),
                fees: invoiceCharge.fees,
                office_nrds_id: invoiceCharge.office_nrds_id
            };
        });
        return invoices;
    }
    getBillingYear(invoicesCharges) {
        const billRunInvoiceCharges = invoicesCharges.filter(invoiceCharge => invoiceCharge.bill_run_id);
        if (billRunInvoiceCharges?.length > 0) {
            return billRunInvoiceCharges.map(billRunInvoiceCharge => billRunInvoiceCharge.billing_year).reduce((a, b) => new Date(a) > new Date(b) ? a : b);
        }
        return moment().year();
    }
    validateAddress(params) {
        return this.addressApiService.validateAddress({
            params
        }).pipe(catchError(err => {
            err.userMessage = 'Error while validating address. Please check your address and try again';
            throw err;
        }));
    }
    getCheckedOptionalFees(optionalFees) {
        return optionalFees.filter(optionalFee => optionalFee.checked);
    }
    getFeeTotal(fee) {
        return round(fee.price + fee.tax, 2);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentService, deps: [{ token: i1.AddressApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentService, providedIn: PaymentServicesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: PaymentServicesModule
                }]
        }], ctorParameters: function () { return [{ type: i1.AddressApiService }]; } });

class PaymentCreditCardService {
    constructor(fb, peopleApiService, authService, paymentService) {
        this.fb = fb;
        this.peopleApiService = peopleApiService;
        this.authService = authService;
        this.paymentService = paymentService;
        this.paymentMethodTypeOptions = [
            { value: 'existing_card', label: 'Use an existing Card' },
            { value: 'add_new_card', label: 'Add a new card' }
        ];
    }
    initPaymentMethodTypeForm() {
        this.paymentMethodTypeForm = this.fb.group({
            paymentMethodType: [this.authService.userPersonId ? 'existing_card' : 'add_new_card']
        });
    }
    initSecondStepForm(paymentDetail) {
        if (this.authService.userPersonId) {
            this.peopleApiService.getPersonPaymentMethods(new RequestParameters('primary_card=true'), this.authService.userPersonId).subscribe(data => {
                const theresNoPaymentMethods = !data.items || data.items.length === 0;
                this.initializeForm(theresNoPaymentMethods, paymentDetail);
            });
        }
        else {
            this.initializeForm(true, paymentDetail);
        }
    }
    initializeForm(theresNoPaymentMethods, paymentDetail) {
        this.secondStepForm = this.fb.group({
            number: [null, [Validators.required, CreditCardValidators.validateCCNumber]],
            nameOnCard: [null, Validators.required],
            expiryDate: [null, [Validators.required]],
            primaryCard: [{ value: theresNoPaymentMethods, disabled: theresNoPaymentMethods }],
            saveInPaymentMethods: [false],
            addressLine1: [paymentDetail?.address_line_1 || null, [Validators.required]],
            addressLine2: [''],
            city: [paymentDetail?.city || null, [Validators.required]],
            state: [paymentDetail?.state || null, [Validators.required]],
            zip: [paymentDetail?.zip_code || null, [Validators.required]]
        });
    }
    nextButtonDisabled(step, termsAndConditionsAccepted) {
        if (step === 2) {
            if (this.paymentMethodTypeForm?.get('paymentMethodType')?.value === 'existing_card') {
                return !this.selectedCard;
            }
            else {
                return !this.secondStepForm || this.secondStepForm.invalid;
            }
        }
        else if (step === 3) {
            return !termsAndConditionsAccepted;
        }
        return false;
    }
    getPaymentCardFields() {
        return {
            type: 'recn',
            payment_method_ref: this.getCardRef()
        };
    }
    getPaymentMethodType() {
        return this.addNewCardSelected() ?
            RecnPaymentMethodType.CreditCard :
            RecnPaymentMethodType.SavedCreditCard;
    }
    getPaymentMethodForm() {
        return this.addNewCardSelected() ?
            this.secondStepForm?.getRawValue() :
            { profileID: this.selectedCard.recn_card_id };
    }
    getCardRef() {
        if (this.addNewCardSelected()) {
            const cardNumber = this.secondStepForm?.get('number')?.value;
            return this.getCCMaskCardNumberValue(cardNumber);
        }
        else {
            return this.selectedCard.cc_mask;
        }
    }
    getCCMaskCardNumberValue(cardNumber) {
        return `${cardNumber.slice(0, 4)}${'*'.repeat(cardNumber.length - 8)}${cardNumber.slice((cardNumber.length) - 4)}`;
    }
    validateNextButton(currentStep) {
        if (currentStep === 2 && this.addNewCardSelected()) {
            const formValue = this.secondStepForm.getRawValue();
            return this.paymentService.validateAddress({
                address1: formValue.addressLine1,
                address2: formValue.addressLine2,
                city: formValue.city,
                state: formValue.state,
                zip5: formValue.zip
            })
                .pipe(map(response => {
                if (response.zip5 !== formValue.zip) {
                    this.secondStepForm.get('zip')?.setValue(response.zip5);
                }
                return response;
            }));
        }
        return of(true);
    }
    addNewCardSelected() {
        return this.paymentMethodTypeForm?.get('paymentMethodType')?.value === 'add_new_card';
    }
    resetForms() {
        this.secondStepForm?.reset();
        this.secondStepForm?.markAsPristine();
        delete this.paymentMethodTypeForm;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentCreditCardService, deps: [{ token: i1$1.FormBuilder }, { token: i2.PeopleApiService }, { token: i3.AuthService }, { token: PaymentService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentCreditCardService, providedIn: PaymentServicesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentCreditCardService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: PaymentServicesModule
                }]
        }], ctorParameters: function () { return [{ type: i1$1.FormBuilder }, { type: i2.PeopleApiService }, { type: i3.AuthService }, { type: PaymentService }]; } });

class PaymentTelecheckService {
    constructor(fb, paymentService) {
        this.fb = fb;
        this.paymentService = paymentService;
    }
    initSecondStepForm(paymentDetail, personInformation) {
        this.secondStepForm = this.fb.group({
            addressLine1: [{ value: paymentDetail?.address_line_1, disabled: false }, Validators.required],
            state: [{ value: paymentDetail?.state, disabled: false }, Validators.required],
            city: [{ value: paymentDetail?.city, disabled: false }, Validators.required],
            zip: [{ value: paymentDetail?.zip_code, disabled: false }, Validators.required],
            dateOfBirth: [{ value: personInformation?.dob, disabled: false }, Validators.required],
            emailAddress: [{
                    value: personInformation?.email,
                    disabled: false
                }, [Validators.required, Validators.pattern(EMAIL_VALIDATOR_REGEXP)]],
            driverLicenseNumber: [{ value: personInformation?.drivers_license_number, disabled: false }, Validators.required],
            driverLicenseState: [{ value: personInformation?.drivers_license_state, disabled: false }, Validators.required],
        });
    }
    initThirdStepForm() {
        this.thirdStepForm = this.fb.group({
            accountName: [{ value: null, disabled: false }, Validators.required],
            checkNumber: [{ value: null, disabled: false }, Validators.required],
            micrNumber: [{ value: null, disabled: false }, Validators.required],
        });
    }
    nextButtonDisabled(step, termsAndConditionsAccepted) {
        if (step === 2) {
            return this.secondStepForm?.invalid;
        }
        else if (step === 3) {
            return this.thirdStepForm.invalid;
        }
        else if (step === 4) {
            return !termsAndConditionsAccepted;
        }
        return false;
    }
    getPaymentCheckFields() {
        return {
            type: 'check',
            payment_method_ref: null
        };
    }
    getPaymentMethodForm() {
        return {
            ...this.secondStepForm.getRawValue(),
            ...this.thirdStepForm.getRawValue()
        };
    }
    validateNextButton(currentStep) {
        if (currentStep === 2) {
            const formValue = this.secondStepForm.getRawValue();
            return this.paymentService.validateAddress({
                address1: formValue.addressLine1,
                address2: formValue.addressLine2,
                city: formValue.city,
                state: formValue.state,
                zip5: formValue.zip
            })
                .pipe(map(response => {
                if (response.zip5 !== formValue.zip) {
                    this.secondStepForm.get('zip')?.setValue(response.zip5);
                }
                return response;
            }));
            ;
        }
        return of(true);
    }
    resetForms() {
        this.secondStepForm?.reset();
        this.secondStepForm?.markAsPristine();
        this.thirdStepForm?.reset();
        this.thirdStepForm?.markAsPristine();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentTelecheckService, deps: [{ token: i1$1.FormBuilder }, { token: PaymentService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentTelecheckService, providedIn: PaymentServicesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: PaymentTelecheckService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: PaymentServicesModule
                }]
        }], ctorParameters: function () { return [{ type: i1$1.FormBuilder }, { type: PaymentService }]; } });

class RecnTransactionUtilsService {
    constructor(
    // private nrdsMembersApiService: NrdsMembersApiService,
    recnTransactionApiService) {
        this.recnTransactionApiService = recnTransactionApiService;
    }
    getActiveNrdsIdOrDefault(memberNrdsId, defaultValue) {
        if (!memberNrdsId) {
            return Promise.resolve(defaultValue);
        }
        else {
            return Promise.resolve(memberNrdsId);
        }
        // return firstValueFrom(this.nrdsMembersApiService.getAll((new RequestParameters(null)).setAdditionalFields({member_nrds_id: memberNrdsId}))
        //   .pipe(
        //     timeout(5000),
        //     catchError(() => {
        //       return defaultValue;
        //     }),
        //     map((members: any) => {
        //       if (!members || members.length === 0 || !['A', 'P', 'S', 'I'].includes(members[0].status)) {
        //         return defaultValue;
        //       } else {
        //         return memberNrdsId;
        //       }
        //     })
        //   ));
    }
    getOperationType(type) {
        const TYPES = {
            'creditCard': 'CreditCardOneTimePurchase',
            'check': 'ACHOneTimePurchase',
            'savedCreditCard': 'CreditCardRecurringPurchase'
        };
        return TYPES[type];
    }
    generateRecnUrl(baseUrl, parameters) {
        // @ts-ignore
        const queryString = Object.keys(parameters).map(key => `${key}=${encodeURIComponent(parameters[key])}`).join('&');
        return `${baseUrl}&${queryString}`;
    }
    getCardType(card_number) {
        const card_first_character = card_number.toString().substr(0, 1);
        const card_first_two_character = card_number.toString().substr(0, 2);
        if (card_first_two_character === '37') {
            return 'American Express';
        }
        else if (card_first_character === '4') {
            return 'Visa';
        }
        else if (card_first_character === '5') {
            return 'Master Card';
        }
        else if (card_first_character === '6') {
            return 'Discover';
        }
        throw new Error('Error while trying to get card type');
    }
    getUrlParams(type, paymentMethodForm) {
        const mappers = {
            [RecnPaymentMethodType$1.Check]: this.getCheckRecnPayment,
            [RecnPaymentMethodType$1.CreditCard]: this.getCardRecnPayment,
            [RecnPaymentMethodType$1.SavedCreditCard]: this.getSavedCardRecnPayment
        };
        // @ts-ignore
        return mappers[type](this, paymentMethodForm);
    }
    createRecnTransaction(operationType, meta = '{}', paymentPayload, personId, billingEntityId, source = 'State Portal') {
        const recnTransactionPayload = {
            operation_type: operationType,
            source,
            meta,
            payment_payload: paymentPayload,
            user_agent: window.navigator.userAgent
        };
        if (personId) {
            recnTransactionPayload.person_id = +personId;
        }
        if (billingEntityId) {
            recnTransactionPayload.billing_entity_id = billingEntityId;
        }
        return firstValueFrom(this.recnTransactionApiService.create(recnTransactionPayload).pipe(this.catchConflict()));
    }
    startProcessingRecnTransaction(recnTransaction, activeMemberNrdsId, billingYear = moment().year(), paymentBody = null, createCcoRecord) {
        const fees = this.getPaymentBodyFees(paymentBody);
        const updateRecnTransactionPayload = {
            member_nrds_id: activeMemberNrdsId,
            status: 'Processing',
            ec_control_number: 0,
            response: '',
            result_code: '0',
            billing_year: billingYear,
            fees,
            return_url: `${window.location.origin}/payment/callback`,
            create_cco_record: createCcoRecord,
            is_member_invoice: true // This was with a TODO: Verify this in the original code
        };
        return firstValueFrom(this.recnTransactionApiService.update(recnTransaction.recn_request_id, updateRecnTransactionPayload));
    }
    async completeRecnTransaction(recnTransaction, recnResponse, createCcoRecord) {
        const response_url = new URL(recnResponse);
        let result_code = response_url.searchParams.get('ResultCode');
        let ec_control_number = response_url.searchParams.get('ECControlNumber');
        const result_message = response_url.searchParams.get('ResultMessage');
        const success = result_code === '0';
        let error = null;
        if (!success) {
            if (result_code === '11') {
                const verifiedRecnTransaction = await firstValueFrom(this.recnTransactionApiService.verifyRecnTransaction(recnTransaction.recn_request_id));
                ec_control_number = verifiedRecnTransaction.ec_control_number.toString();
                result_code = verifiedRecnTransaction.result_code;
                if (result_code !== '0') {
                    // Needed to mock this url so we can process it easier
                    const verification_url = new URL(`https://www.google.com/?` + verifiedRecnTransaction.response);
                    error = { result_message: verification_url.searchParams.get('ResultMessage'), result_code };
                }
            }
            else {
                error = { result_message, result_code };
            }
        }
        const updateRecnTransactionPayload = {
            status: 'Complete',
            ec_control_number,
            response: `?${response_url.searchParams.toString()}`,
            result_code,
            deactivate_cco_record: createCcoRecord !== undefined ? createCcoRecord : undefined
        };
        const response = await firstValueFrom(this.recnTransactionApiService.update(recnTransaction.recn_request_id, updateRecnTransactionPayload));
        if (error) {
            console.error(error);
            throw error;
        }
        else {
            return response;
        }
    }
    getCheckRecnPayment(_this, paymentMethodForm) {
        return {
            AddressLine1: paymentMethodForm.addressLine1 ? paymentMethodForm.addressLine1.substring(0, 30) : paymentMethodForm.addressLine1,
            AddressLine2: '',
            City: paymentMethodForm.city ? paymentMethodForm.city.substring(0, 20) : paymentMethodForm.city,
            State: paymentMethodForm.state,
            ZipCode: paymentMethodForm.zip,
            DriversLicenseNumber: paymentMethodForm.driverLicenseNumber,
            DriversLicenseState: paymentMethodForm.driverLicenseState,
            AccountName: paymentMethodForm.accountName,
            AccountType: 'Electronic+Check',
            EmailAddress: paymentMethodForm.emailAddress,
            DateOfBirth: moment(paymentMethodForm.dateOfBirth).format('MMDDYYYY'),
            CheckNumber: paymentMethodForm.checkNumber,
            MICRNumber: paymentMethodForm.micrNumber
        };
    }
    getCardRecnPayment(_this, paymentMethodForm) {
        return {
            CreditCardType: _this.getCardType(paymentMethodForm.number),
            CreditCardExpirationDate: moment(paymentMethodForm.expiryDate, 'MM/YY').format('MMYY'),
            AccountName: paymentMethodForm.nameOnCard,
            AddressLine1: paymentMethodForm.addressLine1 ? paymentMethodForm.addressLine1.substring(0, 30) : paymentMethodForm.addressLine1,
            AddressLine2: '',
            City: paymentMethodForm.city ? paymentMethodForm.city.substring(0, 20) : paymentMethodForm.city,
            State: paymentMethodForm.state,
            ZipCode: paymentMethodForm.zip,
            CreditCardNumber: paymentMethodForm.number.replace(/ /g, '')
        };
    }
    getSavedCardRecnPayment(_this, paymentMethodForm) {
        return {
            ProfileID: paymentMethodForm.profileID
        };
    }
    getPaymentBodyFees(paymentBody) {
        if (!paymentBody) {
            return null;
        }
        else {
            let fees = [];
            paymentBody.invoices.forEach(invoice => {
                fees = fees.concat(invoice.fees);
                if (invoice.optional_fees.length > 0) {
                    fees = fees.concat(invoice.optional_fees);
                }
            });
            return fees;
        }
    }
    catchConflict() {
        return catchError((error) => {
            return throwError(() => ({ result_message: error.error.error.message, error_code: error.error.status }));
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecnTransactionUtilsService, deps: [{ token: i1.RecnTransactionApiService }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecnTransactionUtilsService, providedIn: PaymentServicesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecnTransactionUtilsService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: PaymentServicesModule
                }]
        }], ctorParameters: function () { return [{ type: i1.RecnTransactionApiService }]; } });

class RecnService {
    constructor(authService, paymentCreditCardService, recnTransactionUtilsService, snackbarService, peopleApiService, componentSidePanelService, sanitizer) {
        this.authService = authService;
        this.paymentCreditCardService = paymentCreditCardService;
        this.recnTransactionUtilsService = recnTransactionUtilsService;
        this.snackbarService = snackbarService;
        this.peopleApiService = peopleApiService;
        this.componentSidePanelService = componentSidePanelService;
        this.sanitizer = sanitizer;
        this.allowUnload = true;
        this.showIframe = false;
        this.currentStep = new BehaviorSubject({
            1: PaymentStepStatus.InProgress,
            2: PaymentStepStatus.Unprocessed,
            3: PaymentStepStatus.Unprocessed
        });
    }
    clearCustomValues() {
        delete this.meta;
        delete this.paymentMemberNrdsId;
        this.allowUnload = true;
    }
    async createPaymentMethod(personId, memberNrdsId, body) {
        let recnRequestId;
        try {
            this.stopNavigation(true);
            await this.testIframeCompatibility();
            let recnTransaction = await this.recnTransactionUtilsService.createRecnTransaction('AddPaymentProfile', this.meta, '[]', personId, this.authService.currentUserEntity?.billing_entity_id, this.source);
            if (recnTransaction) {
                recnRequestId = recnTransaction.recn_request_id;
            }
            const ProfileID = recnTransaction.profile_id.toString();
            const activeMemberNrdsId = await this.recnTransactionUtilsService.getActiveNrdsIdOrDefault(memberNrdsId, recnTransaction.default_member_nrds_id);
            recnTransaction = await this.recnTransactionUtilsService.startProcessingRecnTransaction(recnTransaction, activeMemberNrdsId);
            const recnPaymentMethod = {
                CreditCardType: this.recnTransactionUtilsService.getCardType(body.number),
                CreditCardExpirationDate: moment(body.expiryDate, 'MM/YY').format('MMYY'),
                AccountName: body.nameOnCard,
                AddressLine1: body.addressLine1,
                City: body.city,
                State: body.state,
                ZipCode: body.zip,
                CreditCardNumber: body.number.replace(/ /g, ''),
                ProfileID
            };
            const iframeUrl = this.recnTransactionUtilsService.generateRecnUrl(recnTransaction.recn_url, recnPaymentMethod);
            const iframeResponse = await this.startIframe(iframeUrl);
            await this.recnTransactionUtilsService.completeRecnTransaction(recnTransaction, iframeResponse);
            const paymentMethodPayload = {
                card_type: recnPaymentMethod.CreditCardType,
                cc_mask: this.paymentCreditCardService.getCCMaskCardNumberValue(recnPaymentMethod.CreditCardNumber),
                expiry_date: moment(body.expiryDate, 'MM/YY').format('YYYY-MM-DD'),
                name_on_card: body.nameOnCard,
                person_id: personId,
                primary_card: body.primaryCard,
                recn_card_id: ProfileID,
                success: true
            };
            const payment = await firstValueFrom(this.peopleApiService.createPaymentMethod(personId, paymentMethodPayload));
            return payment;
        }
        catch (error) {
            throw error;
        }
        finally {
            this.stopNavigation(false);
        }
    }
    async processPayment(type, personId, memberNrdsId, paymentPayload, paymentMethodForm, billingYear, createCcoRecord) {
        if (!this.onPaymentCompleted) {
            throw new Error('Need to define onPaymentCompleted');
        }
        this.currentStep.next({
            1: PaymentStepStatus.InProgress,
            2: PaymentStepStatus.Unprocessed,
            3: PaymentStepStatus.Unprocessed
        });
        let recnRequestId;
        try {
            await this.testIframeCompatibility();
            if (type === RecnPaymentMethodType$1.CreditCard && paymentMethodForm.saveInPaymentMethods) {
                await this.createPaymentMethod(personId, memberNrdsId, paymentMethodForm);
            }
            this.stopNavigation(true);
            const operationType = this.recnTransactionUtilsService.getOperationType(type);
            let recnTransaction = await this.recnTransactionUtilsService.createRecnTransaction(operationType, this.meta, JSON.stringify(paymentPayload), personId, this.authService.currentUserEntity?.billing_entity_id, this.source);
            recnRequestId = recnTransaction?.recn_request_id;
            const activeMemberNrdsId = await this.recnTransactionUtilsService.getActiveNrdsIdOrDefault(memberNrdsId, recnTransaction.default_member_nrds_id);
            const defaultMemberNrdsId = recnTransaction.default_member_nrds_id;
            recnTransaction = await this.recnTransactionUtilsService.startProcessingRecnTransaction(recnTransaction, activeMemberNrdsId, billingYear, paymentPayload, createCcoRecord);
            if (this.onRecnTransactionStartProcessing) {
                await firstValueFrom(this.onRecnTransactionStartProcessing({
                    ...recnTransaction,
                    default_member_nrds_id: defaultMemberNrdsId
                }));
            }
            const urlParams = this.recnTransactionUtilsService.getUrlParams(type, paymentMethodForm);
            const iframeUrl = await this.recnTransactionUtilsService.generateRecnUrl(recnTransaction.recn_url, urlParams);
            this.currentStep.next({ 1: PaymentStepStatus.Success, 2: PaymentStepStatus.InProgress });
            const iframeResponse = await this.startIframe(iframeUrl);
            this.currentStep.next({ 2: PaymentStepStatus.Success, 3: PaymentStepStatus.InProgress });
            recnTransaction = await this.recnTransactionUtilsService.completeRecnTransaction(recnTransaction, iframeResponse, createCcoRecord);
            paymentPayload.payment_ref = recnTransaction.ec_control_number.toString();
            await firstValueFrom(this.onPaymentCompleted({ recnTransaction, paymentPayload }));
            this.currentStep.next({ 3: PaymentStepStatus.Success });
            delete this.meta;
            return recnTransaction;
        }
        catch (error) {
            if (this.onPaymentErrored) {
                await firstValueFrom(this.onPaymentErrored({ error, recnRequestId }));
            }
            throw error;
        }
        finally {
            this.stopNavigation(false);
        }
    }
    stopNavigation(value) {
        if (this.componentSidePanelService.panelConfigs && this.componentSidePanelService.panelConfigs.length > 0) {
            this.componentSidePanelService.panelConfigs[this.componentSidePanelService.panelConfigs.length - 1].closeOnBackdropClick = !value;
        }
        this.allowUnload = !value;
    }
    startIframe(url) {
        return new Promise((resolve, reject) => {
            // @ts-ignore
            window.iframeLoaded = (href) => {
                this.showIframe = false;
                this.iframePath = '';
                return resolve(href);
            };
            this.iframePath = this.sanitizer.bypassSecurityTrustResourceUrl(url);
            this.showIframe = true;
        });
    }
    async testIframeCompatibility() {
        return new Promise(async (resolve, reject) => {
            const timeout = setTimeout(() => {
                this.snackbarService.error('Iframes are not supported in this browser or iframes are blocked');
                reject('Error while trying to open iframe');
            }, 10000);
            await this.startIframe(`${window.location.origin}/payment/callback`);
            clearTimeout(timeout);
            resolve(null);
        });
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecnService, deps: [{ token: i1.AuthService }, { token: PaymentCreditCardService }, { token: RecnTransactionUtilsService }, { token: i1.SnackbarService }, { token: i1.PeopleApiService }, { token: i4.ComponentSidePanelService }, { token: i5.DomSanitizer }], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecnService, providedIn: PaymentServicesModule }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "16.2.12", ngImport: i0, type: RecnService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: PaymentServicesModule
                }]
        }], ctorParameters: function () { return [{ type: i1.AuthService }, { type: PaymentCreditCardService }, { type: RecnTransactionUtilsService }, { type: i1.SnackbarService }, { type: i1.PeopleApiService }, { type: i4.ComponentSidePanelService }, { type: i5.DomSanitizer }]; } });

/**
 * Generated bundle index. Do not edit.
 */

export { PaymentCreditCardService, PaymentService, PaymentServicesModule, PaymentTelecheckService, RecnService, RecnTransactionUtilsService };
